import React, { useState, useEffect, useCallback } from 'react';
import { SharedContent, shareService } from '../../services/shareService';
import PlayersReportTable, { PlayerBase } from '../../components/shared/PlayersReportTable';
import { API_URLS } from '../../config/Constants';

interface SharedPlayersReportProps {
  shareData: SharedContent;
}

// Player types for each section - matching original season report
interface PossessionPlayer extends PlayerBase {
  groundBalls: number;
  turnovers: number;
  causedTurnovers: number;
  shotsSaved: number;
  npc: number;
  goalsPlusMinus?: number; // Derived NPC/.25
}

interface OffensePlayer extends PlayerBase {
  shots: number;
  saved: number;
  goals: number;
  assists: number;
  points: number;
  shotPercentage: number;
  sogPercentage: number;
  missedShots: number;
}

interface GoaliePlayer extends PlayerBase {
  shotsOnGoal: number;
  saved: number;
  goalsAllowed: number;
}

interface FaceoffPlayer extends PlayerBase {
  faceoffs: number;
  won: number;
  lost: number;
  winPercentage: number;
  lossPercentage: number;
}

interface PenaltyPlayer extends PlayerBase {
  penalties: number;
}

interface BlockedShotsPlayer extends PlayerBase {
  blockedShots: number;
}

const SharedPlayersReport: React.FC<SharedPlayersReportProps> = ({ shareData }) => {
  const [possessionPlayers, setPossessionPlayers] = useState<PossessionPlayer[]>([]);
  const [offensePlayers, setOffensePlayers] = useState<OffensePlayer[]>([]);
  const [goaliePlayers, setGoaliePlayers] = useState<GoaliePlayer[]>([]);
  const [faceoffPlayers, setFaceoffPlayers] = useState<FaceoffPlayer[]>([]);
  const [penaltyPlayers, setPenaltyPlayers] = useState<PenaltyPlayer[]>([]);
  const [blockedShotsPlayers, setBlockedShotsPlayers] = useState<BlockedShotsPlayer[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Sort configurations for each table
  const [possessionSortConfig, setPossessionSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [offenseSortConfig, setOffenseSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [goalieSortConfig, setGoalieSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [faceoffSortConfig, setFaceoffSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [penaltySortConfig, setPenaltySortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [blockedShotsSortConfig, setBlockedShotsSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });

  const fetchPlayersData = useCallback(async () => {
    setIsLoading(true);
    try {
      let url = '';
      if (shareData.contentType === 'season') {
        url = `${API_URLS.BASE_URL}/share/players/stats?clientId=${shareData.clientId}&seasonId=${shareData.seasonId}`;
      } else {
        url = `${API_URLS.BASE_URL}/share/players/stats?clientId=${shareData.clientId}&seasonId=${shareData.seasonId}&gameId=${shareData.gameId}`;
      }

      const response = await shareService.publicFetch(url);

      if (response.data.success) {
        const rawPlayersData = response.data.data || [];

        // Remove duplicates by keeping only the players with actual stats (gamesPlayed > 1 or has actual stats)
        const uniquePlayers = rawPlayersData.filter((player: any, index: number, array: any[]) => {
          // Find all players with the same name
          const sameNamePlayers = array.filter(
            (p: any) => p.firstName === player.firstName && p.lastName === player.lastName,
          );

          if (sameNamePlayers.length === 1) return true; // No duplicates

          // If there are duplicates, keep the one with more games played or actual stats
          const hasStats =
            player.goals > 0 ||
            player.assists > 0 ||
            player.groundballs > 0 ||
            player.shots > 0 ||
            player.saved > 0 ||
            player.faceoffwins > 0 ||
            player.penalties > 0 ||
            player.causedturnover > 0;
          const hasMoreGames = player.gamesPlayed > 1;

          // Prefer players with more games played, or if same games, prefer ones with stats
          return hasMoreGames || (player.gamesPlayed === 1 && hasStats);
        });

        // Process data using the same logic as original season players report
        processPossessionData(uniquePlayers);
        processOffenseData(uniquePlayers);
        processGoalieData(uniquePlayers);
        processFaceoffData(uniquePlayers);
        processPenaltyData(uniquePlayers);
        processBlockedShotsData(uniquePlayers);
      }
    } catch (error) {
      console.error('Error fetching players data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [shareData]);

  useEffect(() => {
    fetchPlayersData();
  }, [fetchPlayersData]);

  // Data processing functions - matching original season players report logic
  const processPossessionData = (playersData: any[]) => {
    const processed: PossessionPlayer[] = playersData
      .filter((player) => {
        // Exclude pure goalies from possession stats
        const positions = Array.isArray(player.position) ? player.position : [player.position].filter(Boolean);
        const isOnlyGoalie = positions.length === 1 && positions[0] === 'Goalie';

        if (isOnlyGoalie) {
          const hasNonGoalieActivity =
            (player.groundballs || 0) > 0 || (player.turnovers || 0) > 0 || (player.causedturnover || 0) > 0;
          return hasNonGoalieActivity;
        }

        const activity = player.groundballs + player.turnovers + player.causedturnover + player.shotsaved;

        return !!activity;
      })
      .map((player) => {
        const groundBalls = player.groundballs || 0;
        const turnovers = player.turnovers || 0;
        const causedTurnovers = player.causedturnover || 0;
        const shotsSaved = player.shotsaved || 0;

        // NPC Definition: NPC = Ground Balls - Net Turnovers (TO - CTOs) - Shots Saved
        const netTurnovers = turnovers - causedTurnovers;
        const npc = groundBalls - netTurnovers - shotsSaved;
        const goalsPlusMinus = npc * 0.25; // npc / 4

        return {
          playerId: player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions || [],
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          groundBalls,
          turnovers,
          causedTurnovers,
          shotsSaved,
          npc,
          goalsPlusMinus,
        };
      });

    setPossessionPlayers(processed);
  };

  const processOffenseData = (playersData: any[]) => {
    const processed: OffensePlayer[] = playersData
      .filter((player) => {
        const positions = Array.isArray(player.position) ? player.position : [player.position].filter(Boolean);
        const isOnlyGoalie = positions.length === 1 && positions[0] === 'Goalie';

        if (isOnlyGoalie) {
          return player.shots > 0;
        }

        const activity = player.shots + player.shotsaved + player.shotmissed + player.goals + player.assist;

        return activity;
      })
      .map((player) => {
        const shots = player.shots || 0;
        const saved = player.shotsaved || 0;
        const goals = player.goals || 0;
        const assists = player.assist || 0;
        const missedShots = player.shotmissed || 0;
        const points = goals + assists;
        const shotPercentage = shots > 0 ? Number(((goals / shots) * 100).toFixed(0)) : 0;
        const sogPercentage = shots > 0 ? Math.ceil(((goals + saved) / shots) * 100) : 0;

        return {
          playerId: player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions || [],
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          shots,
          saved,
          goals,
          assists,
          points,
          shotPercentage,
          sogPercentage,
          missedShots,
        };
      });

    setOffensePlayers(processed);
  };

  const processGoalieData = (playersData: any[]) => {
    const processed: GoaliePlayer[] = playersData
      .filter((player) => player.shotsongoal)
      .map((player) => {
        const shotsOnGoal = player.shotsongoal || 0;
        const saved = player.saved || 0;
        const goalsAllowed = player.goalsallowed || shotsOnGoal - saved;

        return {
          playerId: player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions || 'G',
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          shotsOnGoal,
          saved,
          goalsAllowed,
        };
      });

    setGoaliePlayers(processed);
  };

  const processFaceoffData = (playersData: any[]) => {
    const processed: FaceoffPlayer[] = playersData
      .filter((player) => (player.faceoffwins || 0) > 0 || (player.faceofflosses || 0) > 0)
      .map((player) => {
        const won = player.faceoffwins || 0;
        const lost = player.faceofflosses || 0;
        const total = won + lost;
        const winPercentage = total > 0 ? Number(((won / total) * 100).toFixed(0)) : 0;
        const lossPercentage = total > 0 ? Number(((lost / total) * 100).toFixed(0)) : 0;

        return {
          playerId: player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          position: player.position || player.positions || [],
          gamesPlayed: player.gamesPlayed || player.gameCount || 0,
          faceoffs: total,
          won,
          lost,
          winPercentage,
          lossPercentage,
        };
      });

    setFaceoffPlayers(processed);
  };

  const processPenaltyData = (playersData: any[]) => {
    const processed: PenaltyPlayer[] = playersData
      .filter((player) => (player.penalties || 0) > 0)
      .map((player) => ({
        playerId: player._id,
        playerNumber: player.no,
        playerName: `${player.firstName} ${player.lastName}`,
        position: player.position || player.positions || [],
        gamesPlayed: player.gamesPlayed || player.gameCount || 0,
        penalties: player.penalties || 0,
      }));

    setPenaltyPlayers(processed);
  };

  const processBlockedShotsData = (playersData: any[]) => {
    const processed: BlockedShotsPlayer[] = playersData
      .filter((p) => (p.blocks || 0) > 0)
      .map((p) => ({
        playerId: p._id,
        playerNumber: p.no,
        playerName: `${p.firstName} ${p.lastName}`,
        position: p.position || p.positions || [],
        gamesPlayed: p.gamesPlayed || p.gameCount || 0,
        blockedShots: p.blocks || 0,
      }));
    setBlockedShotsPlayers(processed);
  };

  // Column definitions - matching original season players report
  const isSpecificGame = shareData.contentType === 'game';
  const gamesPlayedColumn = <T extends PlayerBase>() =>
    isSpecificGame
      ? []
      : [
          {
            header: 'GP',
            key: 'special' as keyof T,
            sortable: true,
            render: (p: T) => p.gamesPlayed ?? 0,
          },
        ];

  const possessionColumns = [
    {
      header: 'Player',
      key: 'special' as keyof PossessionPlayer,
      sortable: true,
      render: (p: PossessionPlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            {p.playerNumber} {p.playerName}
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof PossessionPlayer,
      sortable: true,
      render: (p: PossessionPlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || '-'
        ),
    },
    ...gamesPlayedColumn<PossessionPlayer>(),
    {
      header: 'GB',
      key: 'groundBalls' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'TO',
      key: 'turnovers' as keyof PossessionPlayer,
      sortable: true,
    },
    ...(isSpecificGame
      ? []
      : [
          {
            header: 'TO/PG',
            key: 'special' as keyof PossessionPlayer,
            sortable: true,
            render: (player: PossessionPlayer) => {
              const gamesPlayed = player.gamesPlayed || 1;
              return (player.turnovers / gamesPlayed).toFixed(1);
            },
          },
        ]),
    {
      header: 'CTO',
      key: 'causedTurnovers' as keyof PossessionPlayer,
      sortable: true,
    },
    ...(isSpecificGame
      ? []
      : [
          {
            header: 'CTO/PG',
            key: 'special' as keyof PossessionPlayer,
            sortable: true,
            render: (player: PossessionPlayer) => {
              const gamesPlayed = player.gamesPlayed || 1;
              return (player.causedTurnovers / gamesPlayed).toFixed(1);
            },
          },
        ]),
    {
      header: 'SS',
      key: 'shotsSaved' as keyof PossessionPlayer,
      sortable: true,
    },
    { header: 'NPC', key: 'npc' as keyof PossessionPlayer, sortable: true },
    {
      header: 'G+/-',
      key: 'goalsPlusMinus' as keyof PossessionPlayer,
      sortable: true,
    },
  ];

  const offenseColumns = [
    {
      header: 'Player',
      key: 'special' as keyof OffensePlayer,
      sortable: true,
      render: (p: OffensePlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            {p.playerNumber} {p.playerName}
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof OffensePlayer,
      sortable: true,
      render: (p: OffensePlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || '-'
        ),
    },
    ...gamesPlayedColumn<OffensePlayer>(),
    { header: 'S', key: 'shots' as keyof OffensePlayer, sortable: true },
    {
      header: 'Missed',
      key: 'missedShots' as keyof OffensePlayer,
      sortable: true,
    },
    { header: 'SS', key: 'saved' as keyof OffensePlayer, sortable: true },
    {
      header: 'G',
      key: 'goals' as keyof OffensePlayer,
      sortable: true,
    },
    {
      header: 'S%',
      key: 'shotPercentage' as keyof OffensePlayer,
      sortable: true,
      render: (player: OffensePlayer) => <div className="text-sm">{player.shotPercentage.toFixed(0)}%</div>,
    },
    {
      header: 'SOG%',
      key: 'sogPercentage' as keyof OffensePlayer,
      sortable: true,
      render: (player: OffensePlayer) => <div className="text-sm">{player.sogPercentage}%</div>,
    },
    {
      header: 'A',
      key: 'assists' as keyof OffensePlayer,
      sortable: true,
    },
    { header: 'P', key: 'points' as keyof OffensePlayer, sortable: true },
    ...(isSpecificGame
      ? []
      : [
          {
            header: 'PPG',
            key: 'special' as keyof OffensePlayer,
            sortable: true,
            render: (player: OffensePlayer) => {
              const gamesPlayed = player.gamesPlayed || 1;
              return (player.points / gamesPlayed).toFixed(1);
            },
          },
        ]),
    ...(isSpecificGame
      ? []
      : [
          {
            header: 'GPG',
            key: 'special' as keyof OffensePlayer,
            sortable: true,
            render: (player: OffensePlayer) => {
              const gamesPlayed = player.gamesPlayed || 1;
              return (player.goals / gamesPlayed).toFixed(1);
            },
          },
        ]),
  ];

  const goalieColumns = [
    {
      header: 'Player',
      key: 'special' as keyof GoaliePlayer,
      sortable: true,
      render: (p: GoaliePlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            {p.playerNumber} {p.playerName}
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof GoaliePlayer,
      sortable: true,
      render: (p: GoaliePlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || 'G'
        ),
    },
    ...gamesPlayedColumn<GoaliePlayer>(),
    {
      header: 'SOG',
      key: 'shotsOnGoal' as keyof GoaliePlayer,
      sortable: true,
    },
    {
      header: 'SV',
      key: 'special' as keyof GoaliePlayer,
      sortable: true,
      render: (player: GoaliePlayer) => {
        const savePercentage =
          player.shotsOnGoal > 0 ? Number(((player.saved / player.shotsOnGoal) * 100).toFixed(0)) : 0;
        return (
          <div>
            <div>{player.saved}</div>
            <div className="text-xs text-gray-600">({savePercentage.toFixed(1)}%)</div>
          </div>
        );
      },
    },
    ...(isSpecificGame
      ? []
      : [
          {
            header: 'SPG',
            key: 'special' as keyof GoaliePlayer,
            sortable: true,
            render: (player: GoaliePlayer) => {
              const gamesPlayed = player.gamesPlayed || 1;
              return (player.saved / gamesPlayed).toFixed(1);
            },
          },
        ]),
    {
      header: 'GA',
      key: 'goalsAllowed' as keyof GoaliePlayer,
      sortable: true,
    },
    ...(isSpecificGame
      ? []
      : [
          {
            header: 'GAPG',
            key: 'special' as keyof GoaliePlayer,
            sortable: true,
            render: (player: GoaliePlayer) => {
              const gamesPlayed = player.gamesPlayed || 1;
              return (player.goalsAllowed / gamesPlayed).toFixed(1);
            },
          },
        ]),
  ];

  const faceoffColumns = [
    {
      header: 'Player',
      key: 'special' as keyof FaceoffPlayer,
      sortable: true,
      render: (p: FaceoffPlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            {p.playerNumber} {p.playerName}
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof FaceoffPlayer,
      sortable: true,
      render: (p: FaceoffPlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || '-'
        ),
    },
    ...gamesPlayedColumn<FaceoffPlayer>(),
    {
      header: 'FO',
      key: 'faceoffs' as keyof FaceoffPlayer,
      sortable: true,
    },
    {
      header: 'W',
      key: 'won' as keyof FaceoffPlayer,
      sortable: true,
    },
    {
      header: 'W%',
      key: 'winPercentage' as keyof FaceoffPlayer,
      sortable: true,
      render: (player: FaceoffPlayer) => {
        const winPercentage = player.faceoffs > 0 ? Number(((player.won / player.faceoffs) * 100).toFixed(0)) : 0;
        return <div>{winPercentage.toFixed(0)}%</div>;
      },
    },
    {
      header: 'L',
      key: 'lost' as keyof FaceoffPlayer,
      sortable: true,
    },
  ];

  const penaltyColumns = [
    {
      header: 'Player',
      key: 'special' as keyof PenaltyPlayer,
      sortable: true,
      render: (p: PenaltyPlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            {p.playerNumber} {p.playerName}
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof PenaltyPlayer,
      sortable: true,
      render: (p: PenaltyPlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || '-'
        ),
    },
    ...gamesPlayedColumn<PenaltyPlayer>(),
    {
      header: 'Penalties',
      key: 'penalties' as keyof PenaltyPlayer,
      sortable: true,
    },
  ];

  const blockedShotsColumns = [
    {
      header: 'Player',
      key: 'special' as keyof BlockedShotsPlayer,
      sortable: true,
      render: (p: BlockedShotsPlayer) => (
        <div className="text-left">
          <div className="font-semibold">
            {p.playerNumber} {p.playerName}
          </div>
        </div>
      ),
    },
    {
      header: 'Position',
      key: 'special' as keyof BlockedShotsPlayer,
      sortable: true,
      render: (p: BlockedShotsPlayer) =>
        Array.isArray(p.position) ? (
          <div className="whitespace-nowrap">
            {p.position
              .map((pos) =>
                pos === 'Long Stick Midfield' ? 'LSM' : pos === 'Short Stick Defensive Midfield' ? 'SSDM' : pos,
              )
              .join(' | ')}
          </div>
        ) : (
          (p.position === 'Long Stick Midfield'
            ? 'LSM'
            : p.position === 'Short Stick Defensive Midfield'
              ? 'SSDM'
              : p.position) || '-'
        ),
    },
    ...gamesPlayedColumn<BlockedShotsPlayer>(),
    {
      header: 'Blocked Shots',
      key: 'blockedShots' as keyof BlockedShotsPlayer,
      sortable: true,
    },
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Content */}
      <div className="px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#05396b] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading players report...</p>
          </div>
        ) : (
          <div className="border border-[#bccedf] rounded-lg shadow-sm overflow-hidden">
            <div className="p-2 sm:p-6">
              <PlayersReportTable
                title="Possession"
                columns={possessionColumns}
                players={possessionPlayers}
                sortConfig={possessionSortConfig}
                onSortChange={setPossessionSortConfig}
              />

              <PlayersReportTable
                title="Offense"
                columns={offenseColumns}
                players={offensePlayers}
                sortConfig={offenseSortConfig}
                onSortChange={setOffenseSortConfig}
              />

              <PlayersReportTable
                title="Goalie"
                columns={goalieColumns}
                players={goaliePlayers}
                sortConfig={goalieSortConfig}
                onSortChange={setGoalieSortConfig}
              />

              <PlayersReportTable
                title="Face-offs"
                columns={faceoffColumns}
                players={faceoffPlayers}
                sortConfig={faceoffSortConfig}
                onSortChange={setFaceoffSortConfig}
              />

              <PlayersReportTable
                title="Penalties"
                columns={penaltyColumns}
                players={penaltyPlayers}
                sortConfig={penaltySortConfig}
                onSortChange={setPenaltySortConfig}
              />

              <PlayersReportTable
                title="Blocked Shots"
                columns={blockedShotsColumns}
                players={blockedShotsPlayers}
                sortConfig={blockedShotsSortConfig}
                onSortChange={setBlockedShotsSortConfig}
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-8 border-t border-gray-200 mt-12">
          <p className="text-sm text-gray-500">
            This is a read-only view. Data is powered by{' '}
            <a
              href="https://www.statdog.live"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#05396b] hover:text-[#0a5490] font-semibold underline"
            >
              STATDOG
            </a>
            .
          </p>
          <p className="text-xs text-gray-400 mt-2">Copyright © 2026 Sports Data Partners, LLC dba STATDOG</p>
          <p className="text-xs text-gray-400 mt-1">Access Count: {shareData.accessCount}</p>
        </div>
      </div>
    </div>
  );
};

export default SharedPlayersReport;
